import { Storage } from '@/enums/storage.enum';
import { TableName } from '@/enums/tableName';
import { Login } from '@/interfaces/login';
import { StructureNode } from '@/interfaces/structureNode';
import { authResource, AuthResource } from '@/resources/auth.resource';
import { profileResource } from '@/resources/profile.resource';
import { config } from '@/utils/config';
import { getTokenData } from '@/utils/tokenData';
import store from '../store';
import { AbstractModule } from './abstract.module';
import checkPermission from './auth/checkPermission';
import checkPermissionForExactEntity from './auth/checkPermissionForExactEntity';

/**
 * Moduł autentykacji
 *
 * @class AuthModule
 * @extends {AbstractModule<AuthResource>}
 */
class AuthModule extends AbstractModule<AuthResource> {
  public modules = { checkPermission, checkPermissionForExactEntity };

  /**
   * Dane zalogowanego użytkownika
   *
   * @memberof AuthModule
   */
  public state = { data: undefined, nodes: [], token: localStorage.getItem(Storage.TOKEN), refreshId: undefined };

  public mutations = {
    /**
     * Zapisuje token
     *
     * @param {*} localState
     * @param {string} token
     */
    saveToken(localState: any, token: string): void {
      localState.token = token;
      localStorage.setItem(Storage.TOKEN, token);
    },

    /**
     * Czyści zapisane dane użytkownika
     *
     * @param {*} localState
     */
    clearToken(localState: any): void {
      localState.token = undefined;
      localStorage.removeItem(Storage.TOKEN);
    },

    /**
     * Zapisuje dane
     *
     * @param {*} localState
     * @param {*} data
     */
    saveData(localState: any, data: any): void {
      localState.data = data;
      localStorage.setItem(Storage.PROFILE_ID, data.id.toString());
    },

    /**
     * Czyści zapisane dane użytkownika
     *
     * @param {*} localState
     */
    clearData(localState: any): void {
      localState.data = undefined;
      localState.nodes = [];
      localStorage.clear();
      sessionStorage.clear();
    },

    /**
     * Zapisuje węzły użytkownika
     *
     * @param {*} localState
     * @param {any[]} nodes
     */
    saveNodes(localState: any, nodes: any[]): void {
      nodes.forEach((node: StructureNode) => {
        node.nameWithTypeDescription = `${node.name}  ${node.typeDescription ? `(${node.typeDescription})` : ''}`;
      });
      localState.nodes = nodes;
    },

    /**
     * Ustawia id timeoutu odświeżającego token
     *
     * @param {*} localState
     * @param {number} refreshId
     */
    setRefreshId(localState: any, refreshId: number): void {
      localState.refreshId = refreshId;
    },

    /**
     * Anuluje timeout odświeżający token
     *
     * @param {*} localState
     * @param {boolean} refreshId
     */
    cancelRefresh(localState: any): void {
      clearTimeout(localState.refreshId);
      localState.refreshId = null;
    }
  };
  public actions = {
    /**
     * Logowanie
     *
     * @param context context
     * @param data dane logowania
     */
    login: async (context: any, data: Login): Promise<void> => {
      const response = await this.resource.login(data.login, data.password);
      context.commit('saveToken', response.token);
      if (response.profile.id) {
        localStorage.setItem(Storage.PROFILE_ID, response.profile.id.toString());
      }
      context.commit('saveData', response.profile);
      context.dispatch('updateTokenOnExpire', response.token);
    },

    /**
     * Wczytuje dane profilu
     *
     * @param {*} context
     * @param {{ force: boolean }} [options]
     * @returns {Promise<void>}
     */
    loadData: async (context: any, options?: { force: boolean }): Promise<void> => {
      if (context.state.data && !options?.force) {
        return;
      }
      await Promise.all([profileResource.get(undefined as any), profileResource.getSecretaryNodes()]).then(
        ([profile, nodes]) => {
          context.commit('saveData', profile);
          context.commit('saveNodes', nodes);
        }
      );
    },

    /**
     * Wylogowanie
     *
     * @param context context
     */
    logout: (context: any) => {
      context.commit('clearToken');
      context.commit('clearPermissions');
      context.commit('clearData');
      context.commit('cancelRefresh');
    },

    /**
     * Zmiana hasła zalogowanego użytkownika
     *
     * @param {*} context
     * @param {*} password
     */
    setPasswordLogged: (context: any, data: { currentPassword: string; password: string }) => {
      const userId = getTokenData()?.user.id as string;
      return this.resource
        .setPasswordLogged({ userId, currentPassword: data.currentPassword, password: data.password })
        .then(() => context.dispatch('refreshToken'));
    },

    /**
     * Usuwanie własnego profilu (wysłanie e-mail)
     *
     * @param {*} context
     * @param {string} password
     * @return {*}  {Promise<boolean>}
     */
    deleteOwn: (context: any, password: string): Promise<boolean> => {
      const userId = getTokenData()?.user.id as string;
      return this.resource.deleteOwn({ password, userId });
    },

    /**
     * Odświeża token
     *
     * @param {*} context
     * @returns {Promise<void>}
     */
    refreshToken: (context: any): Promise<void> => {
      clearTimeout(context.state.refreshId);
      context.commit('setRefreshId', null);
      return this.resource
        .refreshToken()
        .then(token => {
          context.commit('saveToken', token);
          context.dispatch('updateTokenOnExpire', token);
        })
        .catch(err => {
          context.dispatch('updateTokenOnExpire');
          throw err;
        });
    },
    /**
     * Odświeża token, gdy kończy się jego ważność
     *
     * @param {*} context
     * @param {string} token
     * @returns {void}
     */
    updateTokenOnExpire: (context: any, token: string): void => {
      token = token || (localStorage.getItem(Storage.TOKEN) as string);
      if (context.state.refreshId || !token) {
        return;
      }
      const tokenPayload = getTokenData(token);
      if (tokenPayload?.exp) {
        const tokenExpInMs = tokenPayload.exp * 1000;
        const reservedTime = 1000 * 60 * config.jwtTimeToRefresh;
        const timeToExpiration = tokenExpInMs - new Date().getTime() - reservedTime;
        const timeoutId = setTimeout(() => context.dispatch('refreshToken'), timeToExpiration);
        context.commit('setRefreshId', timeoutId);
      }
    }
  };

  public getters = {
    paymentListAvailableStructures: (state: { nodes: StructureNode[] }): StructureNode[] =>
      state.nodes?.filter(node => (node.isSingle && node.parentId) || (!node.isSingle && !node.parentId)) || [],

    paymentListStructures: (): StructureNode[] => {
      const checkedPermissions = store.state.auth.checkPermission.permissions;
      const structures = store.getters['auth/paymentListAvailableStructures'] as StructureNode[];
      return (
        structures.filter(
          structure => !!checkedPermissions[`PAYMENT.LIST_SPECIAL:${TableName.structure_node}:${structure.id}`]
        ) || []
      );
    }
  };
}

export default new AuthModule(authResource);
